import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { RootState } from "../../store/store";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  isPhotorealisticFileType,
  isPreviewFileType,
} from "../../utils/cases/photorealisticScan";
import { isUrisBrand, isVelvetBrand, isVelvetLabToken } from "../../utils/brandLogo";
import { canAccessPhotoRecolour } from "../../utils/employeeRoles";
import { fzPatientNamesFromDetails, isFzCaseId } from "../../utils/fzLab";
import api from "../../utils/api";

// Lazy load heavy components
const CaseInstructions = lazy(() => import("./CaseInstructions"));
const CaseFiles = lazy(() => import("./CaseFiles"));
const Comments = lazy(() => import("./Comments"));
const DownloadFiles = lazy(() => import("./DownloadFiles"));
const Previews = lazy(() => import("./Previews"));
const PhotoRecolour = lazy(() => import("./PhotoRecolour"));
const Milling = lazy(() => import("./Milling"));

interface CaseDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseData: any | null;
}

const TABS = [
  { key: "caseInstructions", label: "Case Instructions" },
  { key: "caseFiles", label: "Case Files" },
  { key: "previews", label: "Previews" },
  { key: "photoRecolour", label: "Photo Recolour" },
  { key: "download", label: "Downloads" },
  { key: "milling", label: "Milling" },
  { key: "comments", label: "Comments" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const CaseDetailsModal: React.FC<CaseDetailsModalProps> = ({
  isOpen,
  onClose,
  caseData,
}) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<TabKey>("comments");
  const [fzPatientNames, setFzPatientNames] = useState<string[]>([]);
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { employee } = useAppSelector((state: RootState) => state.allocationAuth);
  const isVelvet = isVelvetBrand(user);
  const isUris = isUrisBrand(user);

  const hasPhotoRecolourAccess = canAccessPhotoRecolour(
    user?.role,
    employee?.role,
    user?.verifyPhotoRecolour,
  );
  const hasMillingAccess =
    user?.role === "Super Admin" || isVelvetLabToken(user?.token);

  const visibleTabs = useMemo(
    () =>
      TABS.filter((tab) => {
        if (tab.key === "photoRecolour") return hasPhotoRecolourAccess;
        if (tab.key === "milling") return hasMillingAccess;
        return true;
      }),
    [hasPhotoRecolourAccess, hasMillingAccess],
  );

  const { caseFiles = [], fetching } = useAppSelector(
    (state: RootState) => state.fileUpload
  );

  // Fetch files only when modal opens and case_id is available
  useEffect(() => {
    if (caseData?.case_id && isOpen) {
      dispatch(fetchCaseFilesByCaseId({ case_id: caseData.case_id }));
    }
  }, [caseData?.case_id, isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const caseId = caseData?.case_id;
    if (!isOpen || !isFzCaseId(caseId)) {
      setFzPatientNames([]);
      return;
    }

    let cancelled = false;
    api
      .get(`/auth/case/${caseId}`)
      .then((res) => {
        if (cancelled) return;
        setFzPatientNames(
          fzPatientNamesFromDetails(res.data?.data?.details_json),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setFzPatientNames(fzPatientNamesFromDetails(caseData?.details_json));
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, caseData?.case_id, caseData?.details_json]);

  // Parse JSON only once
  const parsedCaseData = useMemo(() => {
    try {
      if (!caseData) return null;
      const raw = caseData.details_json ?? caseData.data;
      return typeof raw === "string" ? JSON.parse(raw) : raw || caseData;
    } catch {
      return caseData;
    }
  }, [caseData]);

  // Pre-filtered files
  const filteredFiles = useMemo(() => {
    const lowerType = (type?: string) => type?.toLowerCase();
    return {
      clientUploads: caseFiles.filter(
        (f) => lowerType(f.file_type) === "client_upload"
      ),
      previews: caseFiles.filter((f) => isPreviewFileType(f.file_type)),
      photoRecolour: caseFiles.filter((f) =>
        isPhotorealisticFileType(f.file_type)
      ),
      downloads: caseFiles.filter(
        (f) => lowerType(f.file_type) === "ts_download"
      ),
    };
  }, [caseFiles]);

  if (!isOpen) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case "caseInstructions":
        return (
          <CaseInstructions
            caseId={caseData?.case_id}
            caseData={parsedCaseData}
          />
        );
      case "caseFiles":
        return (
          <CaseFiles
            caseFilesData={filteredFiles.clientUploads}
            caseId={caseData?.case_id}
            fetching={fetching}
            patientNames={fzPatientNames}
          />
        );
      case "previews":
        return (
          <Previews
            previewData={filteredFiles.previews}
            caseId={caseData?.case_id}
            fetching={fetching}
          />
        );
      case "photoRecolour":
        return (
          <PhotoRecolour
            previewData={filteredFiles.previews}
            recolourData={filteredFiles.photoRecolour}
            caseId={caseData?.case_id}
            fetching={fetching}
          />
        );
      case "download":
        return (
          <DownloadFiles
            downloadData={filteredFiles.downloads}
            caseId={caseData?.case_id}
            fetching={fetching}
          />
        );
      case "milling":
        return (
          <Milling
            downloadData={filteredFiles.downloads}
            fetching={fetching}
            caseData={parsedCaseData}
          />
        );
      case "comments":
        return <Comments caseId={caseData?.case_id || ""} />;
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4 ${
        isUris ? "bg-black/70" : "bg-black/40"
      }`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`w-[95vw] h-[92vh] max-w-[1600px] rounded-xl overflow-hidden border-2 flex flex-col min-h-0 ${
          isUris
            ? "uris-cases-scope border-[#6b2430] bg-[#141517] text-[#c5c7cc]"
            : isVelvet
              ? "velvet-cases-scope border-[#DDDDD8] bg-white"
              : "border-[#DDDDD8] bg-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 justify-between items-center w-full relative">
          <div
            className={`flex text-sm md:text-md font-medium px-2 pt-2 pr-10 md:px-4 md:pr-12 border-b-2 w-full overflow-x-auto ${
              isUris
                ? "border-[#2e3036] bg-[#1a1b1f]"
                : "border-[#DDDDD8] bg-[#F8F8F6]"
            }`}
          >
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                className={`cursor-pointer py-2 whitespace-nowrap ${activeTab === tab.key
                  ? isUris
                    ? "border-b-4 text-white border-[#c31c30] px-1 md:px-2"
                    : isVelvet
                    ? "border-b-4 text-[#2C2C2C] border-[#FCF2E5] px-1 md:px-2"
                    : "border-b-4 text-[#2fa9bf] border-[#2fa9bf] px-1 md:px-2"
                  : isUris
                    ? "text-[#8b8d93] border-b-4 border-transparent px-1 md:px-2"
                    : "text-[#AAAAAA] border-b-4 border-transparent px-1 md:px-2"
                  }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className={`text-md md:text-xl font-bold absolute top-3 right-3 ${
              isUris
                ? "text-[#8b8d93] hover:text-[#ff8b96]"
                : "text-gray-400 hover:text-red-500"
            }`}
          >
            ✕
          </button>
        </div>

        <div
          className={`flex-1 min-h-0 flex flex-col ${activeTab === "comments"
              ? "overflow-hidden px-4 pt-2"
              : "overflow-y-auto overscroll-contain px-4 pb-4 pt-2"
            }`}
        >
          <Suspense
            fallback={
              <div
                className={`text-center py-10 ${
                  isUris ? "text-[#c5c7cc]" : ""
                }`}
              >
                Loading...
              </div>
            }
          >
            <div
              className={
                activeTab === "comments"
                  ? "flex flex-1 min-h-0 flex-col"
                  : "min-h-0"
              }
            >
              {renderTabContent()}
            </div>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default CaseDetailsModal;